import { useEffect } from 'react';
import { X, Building2, Briefcase, User, Globe, AlertTriangle, Loader2, Send } from 'lucide-react';
import type { HubspotPreview } from '../../types/visitReport';

interface HubspotPreviewModalProps {
    preview: HubspotPreview | null;
    isLoading: boolean;
    error: string | null;
    isSyncing: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

/**
 * What the sync will put into HubSpot, shown before it goes.
 *
 * Two things are deliberate here and should not be "tidied up":
 *
 * 1. **`dangerouslySetInnerHTML` with the server's bytes, unaltered.** This is the payload, not a
 *    rendering of it — the same `renderBodyHtml` the sync calls, delivered over preview-hubspot.
 *    Sanitising it client-side was measured and rejected: it rewrites `<hr>` to `<hr />` and
 *    decodes `&#39;`/`&quot;`, so the preview would differ from what is sent on every report.
 *    The safety comes from `escapeHtml` on the server, applied to every dynamic value and proven
 *    field by field in api/src/shared/__tests__/visit-report-render.test.ts.
 *
 * 2. **The header names the target, not just the content.** "It looked right" is worth little if
 *    the entry lands on the wrong company, the wrong deal or the wrong portal. Company, deal,
 *    owner and portal are part of what is being confirmed.
 */
export function HubspotPreviewModal({
    preview,
    isLoading,
    error,
    isSyncing,
    onClose,
    onConfirm,
}: HubspotPreviewModalProps) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const blocked = Boolean(preview?.blockedReason) || Boolean(preview?.activityTypeError) || !preview?.ownerId;

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-xl w-full max-w-3xl my-8 flex flex-col max-h-[90vh]"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200">
                    <div className="min-w-0">
                        <h3 className="text-base font-semibold text-gray-800">This is what goes to HubSpot</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Rendered by the server — the sync sends exactly these bytes.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1 rounded-lg hover:bg-gray-100 text-gray-500 shrink-0"
                        aria-label="Close preview"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-5 py-4 overflow-y-auto min-w-0">
                    {isLoading && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 py-8 justify-center">
                            <Loader2 className="w-4 h-4 animate-spin" /> Building the preview…
                        </div>
                    )}

                    {!isLoading && error && (
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span className="break-words">{error}</span>
                        </div>
                    )}

                    {!isLoading && !error && preview && (
                        <>
                            {/* Where it lands. */}
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm space-y-1.5">
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Goes to
                                </div>
                                <div className="flex items-start gap-2 text-gray-700">
                                    <Building2 className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                                    <span className="break-words">
                                        {preview.associations.company.name || 'Unnamed company'}
                                        <span className="text-gray-400"> · {preview.associations.company.id}</span>
                                    </span>
                                </div>
                                <div className="flex items-start gap-2 text-gray-700">
                                    <Briefcase className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                                    <span className="break-words">
                                        {preview.associations.deal
                                            ? <>{preview.associations.deal.name || `Deal ${preview.associations.deal.id}`}
                                                <span className="text-gray-400"> · {preview.associations.deal.id}</span></>
                                            : <span className="text-gray-400">No deal</span>}
                                    </span>
                                </div>
                                <div className="flex items-start gap-2 text-gray-700">
                                    <User className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                                    <span className="break-words">
                                        {preview.ownerName || <span className="text-red-700">No HubSpot owner linked</span>}
                                        {preview.associations.contacts.length > 0 && (
                                            <span className="text-gray-400">
                                                {' '}· {preview.associations.contacts.length} contact
                                                {preview.associations.contacts.length === 1 ? '' : 's'}:{' '}
                                                {preview.associations.contacts.map(c => c.name).join(', ')}
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <div className="flex items-start gap-2 text-gray-700">
                                    <Globe className="w-4 h-4 mt-0.5 text-gray-400 shrink-0" />
                                    <span className="break-words">
                                        Portal {preview.portalId}
                                        <span className="text-gray-400"> · {preview.writeEnv}</span>
                                        <span className="text-gray-400"> · {preview.activityType}</span>
                                    </span>
                                </div>
                            </div>

                            {/* Why it would not go out — straight from the same guard the sync consults. */}
                            {preview.blockedReason && (
                                <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span className="break-words">
                                        HubSpot writes are switched off right now ({preview.blockedReason}), so
                                        syncing would fail. The preview above is still exactly what would be sent.
                                    </span>
                                </div>
                            )}
                            {preview.activityTypeError && (
                                <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span className="break-words">{preview.activityTypeError}</span>
                                </div>
                            )}
                            {!preview.ownerId && (
                                <div className="mt-3 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span className="break-words">
                                        You are not linked to a HubSpot owner in etl.UserCRM, so the sync will be
                                        refused. Ask IT to add the link.
                                    </span>
                                </div>
                            )}

                            <div className="mt-4">
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                                    Title
                                </div>
                                {/* Plain text on purpose: hs_meeting_title is a plain-text HubSpot property and
                                    the server does not escape it. Rendering it as text is what makes that safe. */}
                                <div className="text-sm text-gray-800 break-words rounded-lg border border-gray-200 px-3 py-2">
                                    {preview.title}
                                </div>
                            </div>

                            <div className="mt-4">
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                                    Body
                                </div>
                                <div
                                    className="prose prose-sm max-w-none text-gray-800 rounded-lg border border-gray-200 px-3 py-2 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:mt-3 prose-headings:mb-1"
                                    dangerouslySetInnerHTML={{ __html: preview.bodyHtml }}
                                />
                                <p className="text-xs text-gray-400 mt-1.5">
                                    {preview.bodyHtml.length} characters. The horizontal rules show here and in
                                    HubSpot, but not in the Customer 360 timeline — that view drops them.
                                </p>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                    >
                        Back to editing
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={isLoading || isSyncing || !preview || blocked}
                        title={blocked ? 'The sync would be refused — see the warning above.' : undefined}
                        className="flex items-center gap-2 px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 text-sm font-semibold"
                    >
                        {isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Send to HubSpot
                    </button>
                </div>
            </div>
        </div>
    );
}
